import React, { useState } from 'react';
import { X, User, Mail, Lock, CheckCircle } from 'lucide-react';
import { api } from '../api';
import './UserProfile.css';

export default function UserProfile({ currentUser, onClose, onUpdateUser }) {
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    password: '',
    confirmPassword: ''
  });
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate password only if changing password
    if (showPasswordFields && formData.password) {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
    }

    try {
      const updateData = {};
      if (formData.name !== currentUser.name) updateData.name = formData.name;
      if (formData.email !== currentUser.email) updateData.email = formData.email;
      // Only include password if user explicitly chose to change it
      if (showPasswordFields && formData.password) updateData.password = formData.password;

      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save');
        setLoading(false);
        return;
      }

      const response = await api.updateProfile(updateData);
      
      setSuccess('Profile updated successfully!');
      
      // Clear password fields and hide them
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      setShowPasswordFields(false);

      // Notify parent of updated user
      if (onUpdateUser && response.user) {
        onUpdateUser(response.user);
      }

      // Close modal after a delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={e => e.stopPropagation()}>
        <div className="user-profile-header">
          <h2 className="user-profile-title">
            <User size={22} />
            Edit Profile
          </h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-profile-content">
          {success && (
            <div className="success-message">
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {success}
            </div>
          )}
          
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-group">
            <label>
              <User size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </div>

          <div className="form-group">
            <label>
              <Mail size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </div>

          {!showPasswordFields && (
            <div className="form-group change-password-toggle">
              <button 
                type="button" 
                className="change-password-link"
                onClick={() => setShowPasswordFields(true)}
              >
                <Lock size={14} />
                Change Password?
              </button>
            </div>
          )}

          {showPasswordFields && (
            <>
              <div className="form-group">
                <label>
                  <Lock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  New Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  minLength={6}
                />
                <p className="password-hint">Minimum 6 characters</p>
              </div>

              <div className="form-group">
                <label>
                  <Lock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  required={showPasswordFields}
                />
              </div>

              <div className="form-group">
                <button 
                  type="button" 
                  className="cancel-password-change"
                  onClick={() => {
                    setShowPasswordFields(false);
                    setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
                    setError('');
                  }}
                >
                  Cancel Password Change
                </button>
              </div>
            </>
          )}
        </form>

        <div className="user-profile-actions">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="btn btn-primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
